import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CalendarDays, Download, Info, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { MatrixTable, type Metric } from '@/components/MatrixTable';
import { MetricToggle, MonthSelect } from '@/components/MonthControls';
import { DataTablePagination } from '@/components/DataTablePagination';
import { EmptyState } from '@/components/EmptyState';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { reportsApi } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { fmtDay } from '@/lib/format';
import { useCan } from '@/features/auth/store';

const PAGE_SIZE = 50;

/** A "Planilha Out.26": afiliados × períodos de um mês (dia a dia com os relatórios diários). */
export function DailyPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const canExport = useCan('reports.export');
  const months = useQuery({ queryKey: ['months'], queryFn: reportsApi.months, staleTime: 60_000 });
  const available = months.data ?? [];

  const month = params.get('month') ?? available[available.length - 1] ?? '';
  const by = (params.get('by') ?? 'affiliate') as 'affiliate' | 'product';
  const metric = (params.get('metric') ?? 'gross') as Metric;
  const search = params.get('q') ?? '';
  const only = params.get('only') === 'active' ? 'active' : 'all';
  const sortBy = params.get('sort') ?? 'netSales';
  const sortDir = params.get('dir') ?? 'desc';
  const page = Number(params.get('page') ?? 1);
  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => setSearchInput(search), [search]);

  const set = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === undefined || v === '') next.delete(k);
      else next.set(k, v);
    }
    if (!('page' in patch)) next.delete('page');
    setParams(next, { replace: true });
  };

  const query = {
    month,
    by,
    search: search || undefined,
    only,
    sortBy,
    sortDir,
    page,
    pageSize: PAGE_SIZE,
  };
  const report = useQuery({
    queryKey: ['reports', 'daily', query],
    queryFn: () => reportsApi.daily(query),
    enabled: !!month,
    placeholderData: (p) => p,
  });
  const exportMut = useMutation({
    mutationFn: () => reportsApi.download('daily', { ...query, metric, page: 1 }, 'diario.xlsx'),
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Falha ao exportar'),
  });

  const r = report.data;
  const columns = (r?.columns ?? []).map((c) => ({
    key: c.key,
    label: c.label,
    hint:
      c.days === 1
        ? `Relatório do dia ${fmtDay(c.start)}`
        : `Relatório de ${fmtDay(c.start)} a ${fmtDay(c.end)} (${c.days} dias)`,
  }));
  const multiDay = r?.columns.filter((c) => c.days > 1) ?? [];

  return (
    <div>
      <PageHeader
        title="Faturamento diário"
        description="Cada coluna é um relatório importado dentro do mês: com os relatórios diários da BuyGoods, uma coluna por dia"
        actions={
          canExport && (
            <Button
              variant="outline"
              onClick={() => exportMut.mutate()}
              disabled={exportMut.isPending || !r?.total}
            >
              {exportMut.isPending ? <Loader2 className="animate-spin" /> : <Download />} Exportar
              Excel
            </Button>
          )
        }
      />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <MonthSelect
          label="Mês"
          months={available}
          value={month}
          onChange={(v) => set({ month: v })}
        />
        <div className="space-y-1">
          <Label className="text-xs">Agrupar por</Label>
          <Select value={by} onValueChange={(v) => set({ by: v })}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="affiliate">Afiliado</SelectItem>
              <SelectItem value="product">Produto</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <MetricToggle value={metric} onChange={(m) => set({ metric: m })} />
        <div className="space-y-1">
          <Label className="text-xs">Ordenar por</Label>
          <Select
            value={`${sortBy}:${sortDir}`}
            onValueChange={(v) => {
              const [s, d] = v.split(':');
              set({ sort: s, dir: d });
            }}
          >
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="netSales:desc">Maior líquido</SelectItem>
              <SelectItem value="grossSales:desc">Maior bruto</SelectItem>
              <SelectItem value="name:asc">Nome (A–Z)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-56 flex-1 space-y-1">
          <Label className="text-xs" htmlFor="daily-q">
            Buscar
          </Label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
            <Input
              id="daily-q"
              placeholder={by === 'product' ? 'Nome do produto' : 'Nome do afiliado'}
              className="pl-8"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && set({ q: searchInput.trim() })}
              onBlur={() => searchInput.trim() !== search && set({ q: searchInput.trim() })}
            />
          </div>
        </div>
        <div className="flex items-center gap-2 pb-2">
          <Switch
            id="daily-only"
            checked={only === 'active'}
            onCheckedChange={(v) => set({ only: v ? 'active' : undefined })}
          />
          <Label htmlFor="daily-only" className="text-xs">
            Só com faturamento
          </Label>
        </div>
      </div>

      {multiDay.length > 0 && (
        <Alert className="mb-4">
          <Info className="size-4" />
          <AlertDescription>
            <p>
              {multiDay.length === 1 ? 'A coluna ' : 'As colunas '}
              <span className="font-medium">{multiDay.map((c) => c.label).join(', ')}</span>
              {multiDay.length === 1
                ? ' vem de um relatório de vários dias e não pode ser aberta por dia.'
                : ' vêm de relatórios de vários dias e não podem ser abertas por dia.'}{' '}
              Importe os relatórios diários da BuyGoods (um por dia) para ver dia a dia.
            </p>
          </AlertDescription>
        </Alert>
      )}

      <div className="rounded-lg border bg-card">
        {months.isLoading || (report.isLoading && !r) ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 10 }).map((_, i) => (
              <Skeleton key={i} className="h-9" />
            ))}
          </div>
        ) : !available.length ? (
          <div className="p-6">
            <EmptyState
              icon={CalendarDays}
              title="Nenhum relatório importado"
              description="Importe relatórios da BuyGoods em Importações."
            />
          </div>
        ) : r ? (
          <>
            <MatrixTable
              columns={columns}
              rows={r.items}
              totals={r.totals}
              metric={metric}
              entityLabel={by === 'product' ? 'Produto' : 'Afiliado'}
              withShare={false}
              onRowClick={
                by === 'affiliate'
                  ? (row) => navigate(`/afiliados/${row.id}?from=${month}&to=${month}`)
                  : undefined
              }
              emptyMessage={
                search ? `Nenhum resultado com "${search}".` : 'Nenhum relatório ativo neste mês.'
              }
            />
            <div className="flex flex-wrap items-center justify-between gap-2 border-t px-2">
              <p className="px-2 text-xs text-muted-foreground">
                {r.columns.length} período(s) importado(s) no mês · {r.totals.entitiesActive}{' '}
                {by === 'product' ? 'produto(s)' : 'afiliado(s)'} com faturamento
              </p>
              <DataTablePagination
                page={r.page}
                pageSize={r.pageSize}
                total={r.total}
                onPageChange={(p) => set({ page: String(p) })}
              />
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
